import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { inMaintenanceQuietWindow } from '@/features/system/maintenance';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';

import {
  ToastContext,
  type Toast,
  type ToastApi,
  type ToastOptions,
  type ToastTone,
} from './toastContext';

const TONES: Record<ToastTone, { icon: typeof Info; className: string; iconClass: string }> = {
  success: {
    icon: CheckCircle2,
    className: 'border-emerald-200 bg-white',
    iconClass: 'text-emerald-600',
  },
  error: { icon: AlertTriangle, className: 'border-red-200 bg-white', iconClass: 'text-red-600' },
  info: { icon: Info, className: 'border-line bg-white', iconClass: 'text-brand-600' },
};

const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  // Long enough to read a reason and decide what to do about it.
  error: 8000,
};

/** At most this many on screen; the oldest drops off rather than stacking. */
const MAX_VISIBLE = 4;

/**
 * Transient confirmation for things that happened without a page change - a
 * bulk update applied, an export started, a save that failed.
 *
 * Notifications are `role="status"` in an `aria-live` region so a screen
 * reader announces them without stealing focus. Errors do not auto-dismiss as
 * quickly as successes, and anything with an action stays until dismissed:
 * an "Undo" that disappears after four seconds is not an undo.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (tone: ToastTone, message: string, options: ToastOptions = {}) => {
      // A save just refused for read-only maintenance already has its own,
      // friendlier toast (see `MaintenanceToaster`); the page's generic
      // "could not save" for the same refusal would only repeat it worse.
      if (tone === 'error' && inMaintenanceQuietWindow()) return;
      const id = nextId.current++;
      setToasts((current) => [
        ...current.slice(-(MAX_VISIBLE - 1)),
        { id, tone, message, detail: options.detail, action: options.action },
      ]);

      if (!options.action) {
        const duration = options.duration ?? DEFAULT_DURATION[tone];
        timers.current.set(
          id,
          window.setTimeout(() => dismiss(id), duration),
        );
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show('success', message, options),
      error: (message, options) => show('error', message, options),
      info: (message, options) => show('info', message, options),
      dismiss,
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div
              className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex flex-col items-center gap-2 p-4 sm:items-end"
              role="region"
              aria-label={t('ui.toast.region')}
            >
              {toasts.map((toast) => {
                const tone = TONES[toast.tone];
                return (
                  <div
                    key={toast.id}
                    role="status"
                    aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
                    className={cn(
                      'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border p-3',
                      'shadow-card motion-safe:animate-[fade-in_150ms_ease-out]',
                      tone.className,
                    )}
                  >
                    <tone.icon
                      className={cn('mt-0.5 size-5 shrink-0', tone.iconClass)}
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">{toast.message}</p>
                      {toast.detail ? (
                        <p className="mt-0.5 text-xs text-muted">{toast.detail}</p>
                      ) : null}
                      {toast.action ? (
                        <button
                          type="button"
                          onClick={() => {
                            toast.action!.onClick();
                            dismiss(toast.id);
                          }}
                          className="mt-1.5 text-xs font-semibold text-brand-600 hover:underline"
                        >
                          {toast.action.label}
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      aria-label={t('common.close')}
                      onClick={() => dismiss(toast.id)}
                      className="grid size-6 shrink-0 place-items-center rounded-md text-muted transition-colors hover:bg-surface hover:text-ink focus:outline-none focus:ring-2 focus:ring-brand-100"
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}
